import React from 'react';
import { Marker, Tooltip, Popup, Polyline, Circle } from 'react-leaflet';
import L from 'leaflet';
import { dailyAmmunitionStatus, getAmmunitionStatusForDay, timePointToDay, ammunitionNodes, ammunitionDistributionCorridors, munitionsStatusColors } from './munitionsData';
import { convoyRouteSegments, alternativeRouteSegment } from './convoyData';
// Create custom icon for ammunition status markers
const createMunitionsStatusIcon = (day: number, percentage: number, status: string) => {
  // Map status to color
  const color = status === 'green' ? '#059669' : status === 'amber' ? '#D97706' : status === 'red' ? '#DC2626' : '#1F2937';
  // Calculate progress bar width based on percentage
  const progressWidth = Math.max(10, Math.min(100, percentage)); // Min 10% for visibility, max 100%
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center; width: 100px;">
        <div style="display: flex; align-items: center; background-color: white; padding: 4px; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); width: 100%;">
          <div style="font-size: 16px; margin-right: 4px;">🎯</div>
          <div style="flex-grow: 1; background-color: #f3f4f6; height: 8px; border-radius: 4px; overflow: hidden;">
            <div style="background-color: ${color}; height: 100%; width: ${progressWidth}%;"></div>
          </div>
          <div style="margin-left: 4px; font-weight: bold; font-size: 12px;">${percentage}%</div>
        </div>
        <div style="background-color: ${color}; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          Day ${day}
        </div>
      </div>
    `,
    className: '',
    iconSize: [100, 50],
    iconAnchor: [50, 25]
  });
};
// Create custom icon for ammunition nodes
const createAmmunitionNodeIcon = (isAlternate: boolean) => {
  return L.divIcon({
    html: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="display: flex; align-items: center; justify-content: center; background-color: white; width: 36px; height: 36px; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2); border: 3px solid ${isAlternate ? '#8B5CF6' : '#DC2626'};">
          <div style="font-size: 18px;">🎯</div>
        </div>
        <div style="background-color: ${isAlternate ? '#8B5CF6' : '#DC2626'}; color: white; font-weight: bold; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-top: 2px;">
          ${isAlternate ? 'ALT' : 'F1'}
        </div>
      </div>
    `,
    className: '',
    iconSize: [36, 58],
    iconAnchor: [18, 40]
  });
};
interface MunitionsLayerProps {
  selectedTimePoint: string;
  useAlternativeRoute?: boolean;
}
export const MunitionsLayer: React.FC<MunitionsLayerProps> = ({
  selectedTimePoint,
  useAlternativeRoute = false
}) => {
  // Convert selected time point to day number
  const currentDay = timePointToDay(selectedTimePoint);
  // Get the route segments based on the selected route
  const routeSegments = convoyRouteSegments.filter(segment => {
    // For day 1, check if we should use the alternative route
    if (segment.day === 1 && useAlternativeRoute) {
      return false;
    }
    return true;
  });
  // Add alternative route if selected
  const allSegments = useAlternativeRoute ? [...routeSegments.filter(segment => segment.day !== 1), alternativeRouteSegment] : routeSegments;
  // Sort segments by day
  const sortedSegments = allSegments.sort((a, b) => a.day - b.day);
  // Filter ammunition nodes based on route
  const visibleNodes = ammunitionNodes.filter(node => useAlternativeRoute ? node.isAlternate : !node.isAlternate);
  // Filter distribution corridors based on route
  const visibleCorridors = ammunitionDistributionCorridors.filter(corridor => {
    const node = visibleNodes.find(node => node.id === corridor.nodeId);
    return !!node;
  });
  return <>
      {/* Render ammunition nodes */}
      {visibleNodes.map(node => <Marker key={`ammo-node-${node.id}`} position={node.coordinates} icon={createAmmunitionNodeIcon(node.isAlternate)} data-id="element-464">
          <Popup data-id="element-465">
            <div className="min-w-[200px]" data-id="element-466">
              <h3 className="font-bold text-base" data-id="element-467">{node.name}</h3>
              <p className="text-sm mt-2" data-id="element-468">{node.description}</p>
              {currentDay >= 5 && <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm" data-id="element-469">
                  <span className="font-medium" data-id="element-470">Resupply Complete</span> -
                  Ammunition levels restored to 100%
                </div>}
              {currentDay >= 1 && currentDay < 5 && <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm" data-id="element-471">
                  <span className="font-medium" data-id="element-472">Next Resupply:</span> Day 5
                </div>}
            </div>
          </Popup>
        </Marker>)}
      {/* Render ammunition distribution corridors */}
      {visibleCorridors.map(corridor => <Polyline key={`ammo-corridor-${corridor.id}`} positions={corridor.coordinates} pathOptions={{
      color: '#DC2626',
      weight: 3,
      opacity: 0.7,
      dashArray: '5, 10'
    }} data-id="element-473">
          <Popup data-id="element-474">
            <div data-id="element-475">
              <h3 className="font-bold" data-id="element-476">{corridor.name}</h3>
              <p className="text-sm mt-1" data-id="element-477">{corridor.description}</p>
              <p className="text-sm mt-2" data-id="element-478">
                <span className="font-medium" data-id="element-479">Status:</span>{' '}
                {currentDay >= 1 ? 'Active' : 'Planned'}
              </p>
            </div>
          </Popup>
        </Polyline>)}
      {/* Render ammunition status markers for each day */}
      {sortedSegments.map(segment => {
      const ammoStatus = getAmmunitionStatusForDay(segment.day);
      // Get the coordinates for the marker - use the last point of each segment
      const markerPosition = segment.coordinates[segment.coordinates.length - 1];
      // Only show days up to the current selected day
      if (segment.day > currentDay && currentDay !== 0) {
        return null;
      }
      return <Marker key={`ammo-status-day-${segment.day}`} position={[markerPosition[0] - 0.05, markerPosition[1] + 0.05]} // Offset slightly from fuel markers
      icon={createMunitionsStatusIcon(segment.day, ammoStatus.percentageOnHand, ammoStatus.status)} data-id="element-480">
            <Popup data-id="element-481">
              <div className="min-w-[200px]" data-id="element-482">
                <h3 className="font-bold text-base" data-id="element-483">
                  Class V Ammo Status - Day {segment.day}
                </h3>
                <div className="mt-2 space-y-2 text-sm" data-id="element-484">
                  <div data-id="element-485">
                    <span className="font-medium" data-id="element-486">Location: </span>
                    {segment.name.split(': ')[1]}
                  </div>
                  <div data-id="element-487">
                    <span className="font-medium" data-id="element-488">Status: </span>
                    <span className={`font-medium ${ammoStatus.status === 'green' ? 'text-green-600' : ammoStatus.status === 'amber' ? 'text-amber-600' : ammoStatus.status === 'red' ? 'text-red-600' : 'text-gray-800'}`} data-id="element-489">
                      {ammoStatus.status === 'green' && '🟢 Green'}
                      {ammoStatus.status === 'amber' && '🟠 Amber'}
                      {ammoStatus.status === 'red' && '🔴 Red'}
                      {ammoStatus.status === 'black' && '⚫ Black'}
                    </span>
                  </div>
                  <div data-id="element-490">
                    <span className="font-medium" data-id="element-491">On Hand: </span>
                    {ammoStatus.percentageOnHand}%
                  </div>
                  <div data-id="element-492">
                    <span className="font-medium" data-id="element-493">Note: </span>
                    {ammoStatus.notes}
                  </div>
                  {segment.day === 4 && <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm" data-id="element-494">
                      <span className="font-medium" data-id="element-495">Action: </span>
                      Prep pre-stage for unit-level resupply
                    </div>}
                  {segment.day === 5 && <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm" data-id="element-496">
                      <span className="font-medium" data-id="element-497">Resupply Complete</span> -
                      Ammunition levels restored to 100%
                    </div>}
                </div>
              </div>
            </Popup>
          </Marker>;
    })}
      {/* Render coverage zones for ammunition nodes */}
      {visibleNodes.map(node => <Circle key={`coverage-${node.id}`} center={node.coordinates} radius={50000} // 50km radius
    pathOptions={{
      color: '#DC2626',
      fillColor: '#DC2626',
      fillOpacity: 0.1,
      weight: 1,
      dashArray: '5, 5'
    }} data-id="element-498">
          <Popup data-id="element-499">
            <div data-id="element-500">
              <h3 className="font-bold" data-id="element-501">{node.name} - Coverage Zone</h3>
              <p className="text-sm mt-1" data-id="element-502">
                Maximum supported range from ammunition supply node
              </p>
            </div>
          </Popup>
        </Circle>)}
      {/* Render a summary table when clicking on the route */}
      {sortedSegments.length > 0 && <Polyline positions={sortedSegments.flatMap(segment => segment.coordinates)} pathOptions={{
      color: 'transparent',
      weight: 20,
      opacity: 0.01
    }} data-id="element-503">
          <Popup data-id="element-504">
            <div className="min-w-[280px]" data-id="element-505">
              <h3 className="font-bold text-base mb-2" data-id="element-506">
                Class V Ammunition Status Summary
              </h3>
              <table className="w-full text-sm border-collapse" data-id="element-507">
                <thead data-id="element-508">
                  <tr className="bg-gray-100" data-id="element-509">
                    <th className="border px-2 py-1 text-left" data-id="element-510">Day</th>
                    <th className="border px-2 py-1 text-left" data-id="element-511">Location</th>
                    <th className="border px-2 py-1 text-left" data-id="element-512">Status</th>
                    <th className="border px-2 py-1 text-right" data-id="element-513">On Hand</th>
                  </tr>
                </thead>
                <tbody data-id="element-514">
                  {dailyAmmunitionStatus.map(status => {
                const segment = sortedSegments.find(s => s.day === status.day);
                const location = segment ? segment.name.split(': ')[1] : '—';
                return <tr key={`summary-day-${status.day}`} className={status.day === 5 ? 'bg-green-50' : ''} data-id="element-515">
                        <td className="border px-2 py-1" data-id="element-516">{status.day}</td>
                        <td className="border px-2 py-1" data-id="element-517">{location}</td>
                        <td className="border px-2 py-1" data-id="element-518">
                          <span className={`inline-block w-3 h-3 rounded-full mr-1 ${status.status === 'green' ? 'bg-green-600' : status.status === 'amber' ? 'bg-amber-600' : status.status === 'red' ? 'bg-red-600' : 'bg-gray-800'}`} data-id="element-519"></span>
                          {status.status.charAt(0).toUpperCase() + status.status.slice(1)}
                        </td>
                        <td className="border px-2 py-1 text-right font-medium" data-id="element-520">
                          {status.percentageOnHand}%
                          {status.day === 5 && <span className="ml-1 text-xs text-green-600" data-id="element-521">
                              (Resupply)
                            </span>}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </Popup>
        </Polyline>}
    </>;
};